/**
 * PurchaseOrderListPage (S3.2) — rebuilt on the house list stack (replaces
 * the codex `purchaseOrders/PurchaseOrderListPage.tsx`).
 *
 * usePurchaseOrdersTable (useAntdTable + pre-first-fetch guard) + poStore
 * filters + useListPage state triad + GenericFilterPopover (status /
 * approval state / vendor). Columns: PO # (bold, highlighted, source SO
 * secondary — the SO→PO ref) / Vendor (主要供應商 tag when prefilled) /
 * Status / Approval (shown only while the >NT$200k gate is pending or
 * rejected — affordance, not status noise) / Order date / Total / 80px
 * fixed action column. Whole-row nav to the detail page; new POs are
 * raised from the Demand Pool (toolbar CTA), never typed in here.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Badge, Button, Dropdown, Input, Table, Tag, Tooltip, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  SearchOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import PurpleTag from '@/components/common/PurpleTag';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import { usePurchaseOrderMutations, usePurchaseOrdersTable } from '@/hooks/usePurchaseOrders';
import { useVendorOptions } from '@/hooks/useVendors';
import { usePoStore } from '@/stores/poStore';
import type { PoApprovalFilter, PoFilters, PoStatusFilter } from '@/stores/poStore';
import { useFeature } from '@/features/flags';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types/api/purchaseOrder';
import { PURCHASE_ORDER_STATUS_ORDER } from '@/types/api/purchaseOrder';
import { buildPoUiStatusView } from '@/lib/procurement/poFigmaStatusMap';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { formatMoney } from '../salesOrders/components/soFormat';

/** Popover-local filter value shape for the PO list. */
interface PoFilterPopoverValues extends FilterValues {
  status?: string;
  approvalStatus?: string;
  vendor?: (string | number)[];
}

/** antd Tag preset per status (SO list precedent; no purple literal). */
const STATUS_TAG_COLOR: Record<PurchaseOrderStatus, string> = {
  draft: 'default',
  sent: 'blue',
  vendor_confirmed: 'geekblue',
  shipped: 'cyan',
  received: 'green',
};

/**
 * draft → sent is allowed once the >NT$200k gate is cleared (or absent).
 * P-08 slice A (sticky 94:3829): pending approval → 待審核 / sendBlockedByApproval.
 */
const canSend = (po: PurchaseOrder): boolean => {
  if (po.status !== 'draft') return false;
  if (!po.approvalRequired) return true;
  // Same gate as buildPoUiStatusView.sendBlockedByApproval + approved-only send.
  return po.approvalStatus === 'approved';
};

const PurchaseOrderListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const demandPoolEnabled = useFeature('procurement.poFromSo');

  const { filters, setSearch, setStatus, setApprovalStatus, setVendorIds, resetFilters } =
    usePoStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const { vendors } = useVendorOptions();
  const { sendPurchaseOrder } = usePurchaseOrderMutations();

  const customFilterCounters = useMemo(
    () => ({
      status: (value: unknown) => (value === 'all' ? 0 : 1),
      approvalStatus: (value: unknown) => (value === 'all' ? 0 : 1),
      vendorIds: (value: unknown) => (Array.isArray(value) ? value.length : 0),
    }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    PoFilters,
    PurchaseOrder
  >({
    useTableHook: usePurchaseOrdersTable as (filters: PoFilters) => TableHookResult<PurchaseOrder>,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('procurement:fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('procurement:filters.allStatuses') },
          ...PURCHASE_ORDER_STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`procurement:status.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'approvalStatus',
        label: t('procurement:fields.approval'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('procurement:filters.allApprovals') },
          { value: 'pending', label: t('procurement:approval.pending') },
          { value: 'rejected', label: t('procurement:approval.rejected') },
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'vendor',
        label: t('procurement:fields.vendor'),
        type: 'multiselect',
        options: vendors.map((vendor) => ({
          value: vendor.vendorId,
          label: vendor.vendorName,
        })),
        placeholder: t('procurement:filters.selectVendor'),
        resetText: t('common:actions.reset'),
      },
    ],
    [t, vendors]
  );

  const initialPopoverFilters: PoFilterPopoverValues = useMemo(
    () => ({
      status: filters.status,
      approvalStatus: filters.approvalStatus,
      vendor: filters.vendorIds,
    }),
    [filters.status, filters.approvalStatus, filters.vendorIds]
  );

  const handleApplyFilters = (values: PoFilterPopoverValues) => {
    setStatus((values.status as PoStatusFilter) || 'all');
    setApprovalStatus((values.approvalStatus as PoApprovalFilter) || 'all');
    setVendorIds((values.vendor || []).map(Number));
  };

  const handleSend = async (po: PurchaseOrder) => {
    try {
      await sendPurchaseOrder(po.id);
      message.success(t('procurement:messages.sent'));
      refresh();
    } catch (error) {
      const { message: detail } = extractApiError(error);
      message.error(detail || t('common:messages.updateFailed'));
    }
  };

  const columns: TableProps<PurchaseOrder>['columns'] = [
    {
      title: t('procurement:fields.poNumber'),
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 200,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.poNumber} searchTerm={filters.search} fallback="-" />
          </span>
          {record.sourceSoNumber && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              <HighlightText
                text={record.sourceSoNumber}
                searchTerm={filters.search}
                fallback=""
              />
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('procurement:fields.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 280,
      render: (value: string, record) => (
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <HighlightText text={value} searchTerm={filters.search} fallback="-" />
          {record.vendorPrefillSource === 'preferred' && (
            <PurpleTag style={{ margin: 0 }}>{t('procurement:fields.preferredVendor')}</PurpleTag>
          )}
        </div>
      ),
    },
    {
      title: t('procurement:fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: PurchaseOrderStatus, record: PurchaseOrder) => {
        // P-08 slice A: primary label stays i18n code status. The Figma sticky
        // 94:3829 lane (status machine only — EXCEL_WINS on fields) is DEV-only
        // traceability: internal design vocabulary never ships to operators
        // (production-artifact rule), and buildPoUiStatusView keeps the mapping.
        const statusTag = (
          <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
            {t(`procurement:status.${status}`)}
          </Tag>
        );
        if (!import.meta.env.DEV) return statusTag;
        const devFigmaLane = buildPoUiStatusView(status, record.approvalStatus).figmaLane;
        return <Tooltip title={'Figma: ' + devFigmaLane}>{statusTag}</Tooltip>;
      },
    },
    {
      title: t('procurement:fields.approval'),
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      width: 140,
      render: (_: unknown, record) =>
        record.approvalStatus === 'pending' ? (
          <Tooltip title={t('procurement:detail.banner.pendingTitle')}>
            <Tag color="orange" style={{ margin: 0 }}>
              {t('procurement:approval.pending')}
            </Tag>
          </Tooltip>
        ) : record.approvalStatus === 'rejected' ? (
          <Tag color="red" style={{ margin: 0 }}>
            {t('procurement:approval.rejected')}
          </Tag>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('procurement:fields.orderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: (value: string) => value || renderDash(),
    },
    {
      title: t('procurement:fields.total'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 160,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.totalAmount)}
        </span>
      ),
    },
    {
      title: t('procurement:fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: t('common:actions.view'),
                icon: <EyeOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/purchase-orders/${record.id}`);
                },
              },
              // The >NT$200k gate blocks send — hide, don't disable.
              ...(canSend(record)
                ? [
                    {
                      key: 'send',
                      label: t('procurement:actions.send'),
                      icon: <SendOutlined />,
                      onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                        e.domEvent.stopPropagation();
                        void handleSend(record);
                      },
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('procurement:actions.actionsFor', { poNumber: record.poNumber })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('procurement:list.title')}
        </h1>
      </div>

      {/* Search + filter + demand-pool toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('procurement:searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<PoFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleApplyFilters}
          initialFilters={initialPopoverFilters}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        {demandPoolEnabled && (
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            size="large"
            onClick={() => navigate('/auth/purchase-orders/demand-pool')}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('procurement:openDemandPool')}
          </Button>
        )}
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('procurement:states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<PurchaseOrder>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/purchase-orders/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('procurement:states.noResults')}
                actionLabel={t('procurement:states.clearFilters')}
                onAction={resetFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('procurement:states.noPos')}
                actionLabel={
                  demandPoolEnabled ? t('procurement:states.startFromDemand') : undefined
                }
                onAction={
                  demandPoolEnabled
                    ? () => navigate('/auth/purchase-orders/demand-pool')
                    : undefined
                }
                actionIcon={<ThunderboltOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1130 }}
        />
      </TableContainer>
    </div>
  );
};

export default PurchaseOrderListPage;
